/**
 * InstructorInterviewReviews — /instructor/interview-reviews
 * Ekran 1: Mulakat Degerlendirmeleri — prototip birebir layout
 *
 * Prototip ref: kariyer-online-dashboard.dc.html #isMulakat
 * Layout: max-w-[1180px] px-10 pt-[34px] pb-14
 * 4 stat karti + toggle (Bekleyenler/Tumu) + session queue listesi
 */
import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, ChevronRight,
  CheckCircle, AlertCircle, RefreshCw, ArrowLeft,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useInstructorPendingReviews } from '../hooks/cabin/useInstructorPendingReviews';
import { useInstructorAllReviews } from '../hooks/cabin/useInstructorAllReviews';
const TABS = ['pending', 'all'];

// ── Stat card (prototip: 38px icon box, 32px sayı, border #E6ECF3, shadow) ──
function StatCard({ iconBg, iconStroke, icon, value, label, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        background: '#fff',
        border: '1px solid #E6ECF3',
        borderRadius: '18px',
        padding: '20px',
        boxShadow: '0 8px 22px rgba(16,40,73,.04)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{
          width: '38px', height: '38px', borderRadius: '11px',
          background: iconBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke={iconStroke}
            strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {icon}
          </svg>
        </div>
      </div>
      <div style={{
        fontFamily: "'Fredoka','Nunito',sans-serif",
        fontSize: '32px', fontWeight: '600', marginTop: '12px', lineHeight: '1',
        color: '#13233F',
      }}>
        {value}
      </div>
      <div style={{ color: '#6B7B92', fontSize: '13.5px', marginTop: '5px', fontWeight: '600' }}>
        {label}
      </div>
    </div>
  );
}

// ── Session row (prototip queue satiri) ────────────────────────────────────────
function SessionRow({ review, onInspect, onScore, t, lang }) {
  const isPending = review.status !== 'scored';

  const formattedDate = useMemo(() => {
    const d = review.submitted_at || review.created_at;
    if (!d) return '—';
    try {
      return new Intl.DateTimeFormat(lang === 'tr' ? 'tr-TR' : 'en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
      }).format(new Date(d));
    } catch { return '—'; }
  }, [review.submitted_at, review.created_at, lang]);

  const initials = (review.user_name || '?').split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const diffLabel = (() => {
    const labels = lang === 'tr'
      ? ['', 'Çok Kolay', 'Kolay', 'Orta', 'Zor', 'Çok Zor']
      : ['', 'Very Easy', 'Easy', 'Medium', 'Hard', 'Very Hard'];
    const d = review.difficulty;
    return d ? `${lang === 'tr' ? 'Zorluk' : 'Difficulty'} ${d} · ${labels[d] || ''}` : '—';
  })();

  const langLabel = (() => {
    const l = (review.language || 'tr').toLowerCase();
    if (l === 'tr') return t('kariyer.lang.tr') || 'Türkçe';
    return t('kariyer.lang.en') || 'English';
  })();

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '18px',
      padding: '16px 22px', borderBottom: '1px solid #F1F4F8',
    }}>
      {/* Avatar */}
      <div style={{
        width: '46px', height: '46px', flexShrink: 0, borderRadius: '13px',
        background: 'linear-gradient(135deg,#1F3357,#13233F)',
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: '800', fontSize: '15px',
      }}>
        {initials}
      </div>

      {/* Isim + test */}
      <div style={{ minWidth: '170px' }}>
        <div style={{ fontWeight: '800', fontSize: '15px', color: '#13233F' }}>
          {review.user_name || '—'}
        </div>
        <div style={{ fontSize: '13px', color: '#8593A6', marginTop: '2px' }}>
          {t('instructor.reviews.title')}
        </div>
      </div>

      {/* Zorluk / tarih / dil */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '160px' }}>
        <div style={{ fontSize: '12.5px', color: '#6B7B92', fontWeight: '600' }}>{diffLabel}</div>
        <div style={{ fontSize: '12px', color: '#9AA8BC' }}>
          {formattedDate} · {langLabel}
        </div>
      </div>

      {/* Durum rozeti */}
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '7px' }}>
        {isPending ? (
          <span style={{
            background: '#FFF4E2', color: '#C9760A', padding: '6px 13px',
            borderRadius: '99px', fontSize: '12.5px', fontWeight: '800',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#F49B1E', flexShrink: 0 }} />
            {lang === 'tr' ? 'Bekliyor' : 'Pending'}
          </span>
        ) : (
          <span style={{
            background: '#E6F6EC', color: '#1F9A4D', padding: '6px 13px',
            borderRadius: '99px', fontSize: '12.5px', fontWeight: '800',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#2FB85E', flexShrink: 0 }} />
            {lang === 'tr' ? 'Değerlendirildi' : 'Evaluated'}
          </span>
        )}
      </div>

      {/* Butonlar — prototip: ghost "İncele" + cyan filled "Puanla"/"Düzenle" */}
      <div style={{ display: 'flex', gap: '9px' }}>
        {/* İncele — beyaz border ghost */}
        <motion.button
          type="button"
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => onInspect(review.session_id)}
          style={{
            fontFamily: 'inherit', fontSize: '13.5px', fontWeight: '700',
            padding: '9px 17px', borderRadius: '10px',
            border: '1.5px solid #DCE4ED',
            background: '#fff', color: '#13233F', cursor: 'pointer',
          }}
        >
          {t('instructor.reviews.inspect')}
        </motion.button>
        {/* Puanla (pending) / Düzenle (scored) — cyan filled */}
        <motion.button
          type="button"
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => onScore(review.session_id)}
          style={{
            fontFamily: 'inherit', fontSize: '13.5px', fontWeight: '700',
            padding: '9px 17px', borderRadius: '10px', border: 'none',
            background: '#14B4CC', color: '#fff', cursor: 'pointer',
            boxShadow: '0 6px 14px -4px rgba(20,180,204,.6)',
          }}
        >
          {isPending
            ? (lang === 'tr' ? 'Puanla' : 'Score')
            : (lang === 'tr' ? 'Düzenle' : 'Edit')}
        </motion.button>
      </div>
    </div>
  );
}

// ── Stats hesapla ──────────────────────────────────────────────────────────────
// avgScore: backend avg_ccri_total (0e16799) — TÜM scored kayıtların sayfa-bağımsız ortalaması.
//   Backend null döndürürse (eski backend / henüz yüklenmemiş) mevcut sayfadaki allReviews
//   üzerinden client-side hesap fallback olarak kullanılır.
//   NOT: 0 geçerli bir ortalamadır — ?? operatörü ile korunur (falsy olan 0 fallback'e düşmez).
// weeklyCompleted: backend this_week_completed değeri varsa onu kullan (scored_at bazlı,
//   daha doğru). Backend null/undefined döndürürse (eski backend / yüklenmemiş durum)
//   allReviews'taki scored kayıtların submitted_at son 7 gün olanlarını say (fallback).
function useReviewStats(pendingReviews, allReviews, allTotal, backendThisWeekCompleted, backendAvgCcriTotal) {
  return useMemo(() => {
    const pendingCount = pendingReviews.length;
    const totalCount = allTotal ?? null;

    // Ortalama skor: önce backend'in sayfa-bağımsız avg_ccri_total alanını kullan (0e16799).
    // Alan null ise (eski backend veya henüz yüklenmemişse) mevcut sayfadaki scored
    // kayıtlardan client-side hesapla (fallback).
    const scoredWithCcri = allReviews.filter(r => r.status === 'scored' && r.ccri_total != null);
    const clientAvgScore = scoredWithCcri.length
      ? Math.round(scoredWithCcri.reduce((sum, r) => sum + r.ccri_total, 0) / scoredWithCcri.length)
      : null;
    // backendAvgCcriTotal === 0 geçerli değer; ?? ile korunur (0 fallback'e düşmez)
    const avgScore = backendAvgCcriTotal != null
      ? Math.round(backendAvgCcriTotal)
      : clientAvgScore;

    // Bu hafta tamamlanan: önce backend'in this_week_completed alanını kullan
    // (scored_at bazlı — commit f212bcb). Alan null/undefined ise submitted_at
    // üzerinden son 7 günlük frontend fallback hesabına düş.
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const fallbackWeeklyCompleted = allReviews.filter(r => {
      if (r.status !== 'scored') return false;
      const ts = r.submitted_at ? new Date(r.submitted_at).getTime() : NaN;
      return !isNaN(ts) && ts >= sevenDaysAgo;
    }).length;
    const weeklyCompleted = backendThisWeekCompleted ?? fallbackWeeklyCompleted;

    return { pendingCount, totalCount, avgScore, weeklyCompleted };
  }, [pendingReviews, allReviews, allTotal, backendThisWeekCompleted, backendAvgCcriTotal]);
}

// ── Ana sayfa ─────────────────────────────────────────────────────────────────
export default function InstructorInterviewReviews() {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState('pending');
  const [allPage, setAllPage] = useState(1);

  const {
    reviews: pendingReviews, loading: pendingLoading, error: pendingError, refetch: refetchPending,
  } = useInstructorPendingReviews();

  const {
    reviews: allReviews, total: allTotal, thisWeekCompleted: backendThisWeekCompleted,
    avgCcriTotal: backendAvgCcriTotal,
    loading: allLoading, error: allError, refetch: refetchAll,
  } = useInstructorAllReviews({ page: allPage });

  const stats = useReviewStats(pendingReviews, allReviews, allTotal, backendThisWeekCompleted, backendAvgCcriTotal);

  // Scoring sayfasindan geri dondugunde refetch
  const refetchHandledRef = useRef(false);
  useEffect(() => {
    if (location.state?.refetch && !refetchHandledRef.current) {
      refetchHandledRef.current = true;
      refetchPending();
      refetchAll();
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (!location.state?.refetch) {
      refetchHandledRef.current = false;
    }
  }, [location.state, location.pathname, refetchPending, refetchAll, navigate]);

  // İncele → puanlama sayfasını görüntüleme modunda aç
  // mode=view (veya param yoksa) → readOnly=true, yenileme sonrası korunur
  const handleInspect = (sessionId) => {
    navigate(`/instructor/interview-reviews/${sessionId}/score?mode=view`);
  };

  // Puanla / Düzenle → edit modu — mode=edit query param ile kalıcı, refresh'te kaybolmaz
  const handleScore = (sessionId) => {
    navigate(`/instructor/interview-reviews/${sessionId}/score?mode=edit`);
  };

  const activeList = activeTab === 'pending' ? pendingReviews : allReviews;
  const isLoading  = activeTab === 'pending' ? pendingLoading  : allLoading;
  const hasError   = activeTab === 'pending' ? pendingError    : allError;
  const refetch    = activeTab === 'pending' ? refetchPending  : refetchAll;

  return (
    <div style={{ padding: '34px 40px 56px', maxWidth: 'var(--interview-content-max-w)', width: '100%' }}>

      {/* ── Baslik + toggle ──────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
        gap: '20px', flexWrap: 'wrap', marginBottom: '26px',
      }}>
        <div>
          {/* Geri butonu */}
          <button
            type="button"
            onClick={() => navigate('/instructor')}
            className="inline-flex items-center gap-1.5 mb-4 px-3 py-1.5 rounded-xl border-2 border-gray-200 bg-white text-gray-600 font-semibold text-sm hover:bg-gray-50 hover:border-gray-300 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] shadow-[1px_1px_0px_0px_rgba(0,0,0,0.06)]"
            aria-label={t('common.back')}
          >
            <ArrowLeft size={16} aria-hidden="true" />
            {t('common.back')}
          </button>
          <div style={{
            fontSize: '12.5px', fontWeight: '800', letterSpacing: '1px',
            color: '#19C0CE', marginBottom: '7px',
          }}>
            {lang === 'tr' ? 'EĞİTMEN PANELİ' : 'INSTRUCTOR PANEL'}
          </div>
          <h1 style={{
            fontFamily: "'Fredoka','Nunito',sans-serif",
            fontWeight: '600', fontSize: '33px', margin: '0', color: '#13233F',
          }}>
            {t('instructor.reviews.title')}
          </h1>
          <p style={{ margin: '7px 0 0', color: '#6B7B92', fontSize: '15px' }}>
            {t('instructor.reviews.subtitle')}
          </p>
        </div>
        {/* Toggle */}
        <div style={{
          display: 'flex', gap: '9px', background: '#fff',
          border: '1px solid #E6ECF3', borderRadius: '12px', padding: '5px',
        }}>
          {TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => { setActiveTab(tab); setAllPage(1); }}
              aria-pressed={activeTab === tab}
              style={{
                padding: '7px 14px', borderRadius: '8px', border: 'none',
                background: activeTab === tab ? '#14B4CC' : 'transparent',
                color: activeTab === tab ? '#fff' : '#6B7B92',
                fontFamily: 'inherit', fontWeight: '700', fontSize: '13.5px', cursor: 'pointer',
                transition: 'background .15s',
              }}
            >
              {tab === 'pending'
                ? (lang === 'tr' ? 'Bekleyenler' : 'Pending')
                : (lang === 'tr' ? 'Tümü' : 'All')}
              {tab === 'pending' && pendingReviews.length > 0 && (
                <span style={{
                  marginLeft: '6px', background: activeTab === 'pending' ? 'rgba(255,255,255,.3)' : '#FFF4E2',
                  color: activeTab === 'pending' ? '#fff' : '#C9760A',
                  fontSize: '11px', fontWeight: '800', padding: '1px 6px', borderRadius: '99px',
                }}>
                  {pendingReviews.length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── 4 Stat karti ──────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '16px', marginBottom: '26px' }}>
        <StatCard
          testId="stat-pending"
          iconBg="#FFF4E2"
          iconStroke="#F49B1E"
          icon={<><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>}
          value={pendingLoading ? '…' : stats.pendingCount}
          label={t('instructor.reviews.stats.pending')}
        />
        <StatCard
          testId="stat-weekly"
          iconBg="#E6F6EC"
          iconStroke="#2FB85E"
          icon={<path d="M20 6 9 17l-5-5"/>}
          value={allLoading ? '…' : stats.weeklyCompleted}
          label={lang === 'tr' ? 'Bu hafta tamamlanan' : 'Completed this week'}
        />
        <StatCard
          iconBg="#E4F7FA"
          iconStroke="#14B4CC"
          icon={<><path d="m12 15 8.5-8.5a2.1 2.1 0 0 0-3-3L9 12"/><path d="M9 12v3h3"/><path d="M5 21h14"/></>}
          value={stats.avgScore != null ? `${stats.avgScore}` : '—'}
          label={lang === 'tr' ? 'Ortalama mülakat skoru' : 'Avg. score'}
        />
        <StatCard
          iconBg="#EEF0FF"
          iconStroke="#5848C2"
          icon={<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/></>}
          value={allLoading ? '…' : (allTotal != null ? allTotal : '—')}
          label={lang === 'tr' ? 'Aktif öğrenci' : 'Active students'}
        />
      </div>

      {/* ── Oturum kuyrugu listesi ────────────────────────────────────── */}
      <div style={{
        background: '#fff', border: '1px solid #E6ECF3',
        borderRadius: '20px', overflow: 'hidden',
        boxShadow: '0 8px 26px rgba(16,40,73,.05)',
      }}>
        {/* Liste baslik */}
        <div style={{
          padding: '18px 22px', borderBottom: '1px solid #EEF2F6',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{
            fontFamily: "'Fredoka','Nunito',sans-serif",
            fontWeight: '600', fontSize: '17px', color: '#13233F',
          }}>
            {lang === 'tr' ? 'Oturum kuyruğu' : 'Session queue'}
          </div>
          <div style={{ fontSize: '13px', color: '#8593A6' }}>
            {isLoading ? '…' : `${activeList.length} ${lang === 'tr' ? 'oturum' : 'sessions'}`}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {isLoading ? (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {[1, 2, 3].map(i => (
                <div key={i} style={{ height: '78px', borderBottom: '1px solid #F1F4F8', background: 'linear-gradient(90deg,#f7f7f8,#eee,#f7f7f8)', animation: 'pulse 1.4s infinite' }} />
              ))}
            </motion.div>
          ) : hasError ? (
            <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              style={{ padding: '40px', textAlign: 'center' }}>
              <AlertCircle size={32} style={{ color: '#F43F5E', margin: '0 auto 12px' }} />
              <p style={{ color: '#6B7B92', fontSize: '14px' }}>{hasError}</p>
              <button type="button" onClick={refetch}
                style={{ marginTop: '12px', padding: '8px 18px', borderRadius: '10px', border: '1.5px solid #DCE4ED', background: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontWeight: '700', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <RefreshCw size={14} />
                {t('cv.status.error_retry')}
              </button>
            </motion.div>
          ) : activeList.length === 0 ? (
            <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              style={{ padding: '40px', textAlign: 'center', color: '#9AA8BC' }}>
              <CheckCircle size={36} style={{ margin: '0 auto 12px', opacity: .4 }} />
              <p style={{ fontWeight: '600' }}>
                {activeTab === 'pending' ? t('instructor.reviews.no_pending') : t('instructor.reviews.no_sessions')}
              </p>
            </motion.div>
          ) : (
            <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {activeList.map(r => (
                <SessionRow key={r.session_id} review={r} onInspect={handleInspect} onScore={handleScore} t={t} lang={lang} />
              ))}
              {/* Pagination (all tab) */}
              {activeTab === 'all' && allTotal > 10 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '16px' }}>
                  <button type="button" onClick={() => setAllPage(p => Math.max(1, p - 1))} disabled={allPage === 1}
                    style={{ width: '36px', height: '36px', borderRadius: '10px', border: '1.5px solid #DCE4ED', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: allPage === 1 ? .4 : 1 }}>
                    <ChevronLeft size={16} />
                  </button>
                  <span style={{ fontSize: '13.5px', fontWeight: '700', color: '#6B7B92' }}>{allPage}</span>
                  <button type="button" onClick={() => setAllPage(p => p + 1)} disabled={allReviews.length < 10}
                    style={{ width: '36px', height: '36px', borderRadius: '10px', border: '1.5px solid #DCE4ED', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: allReviews.length < 10 ? .4 : 1 }}>
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
